export const MES_COMMANDES_QUICK_VIEW = {
  ALL: 'all',
  COMMANDES: 'commandes',
  RDV: 'rdv',
  /** Filtre UX : statut EN_ATTENTE déjà utilisé côté badge / compteurs ops */
  ACTIONS: 'actions',
};

/** Action commerçant attendue — statut existant EN_ATTENTE uniquement */
export function isMesCommandesActionRequise(commande) {
  return String(commande?.statut || '').toUpperCase() === 'EN_ATTENTE';
}

export const MES_COMMANDES_PLANNING_PATH = '/planning';

/** Wording Admin Mes Commandes — pas de changement backend / enums. */
export const MES_COMMANDES_PRESTATIONS_RDV_FILTER_LABEL = 'Prestations & RDV';
export const MES_COMMANDES_PRESTATIONS_RDV_BLOCK_TITLE = 'Prestations & rendez-vous planifiés';
/** En-tête 1re colonne tableau RDV (aligné sémantique filtre, colonne = réf + badge). */
export const MES_COMMANDES_PRESTATIONS_RDV_TABLE_REF_LABEL = 'Presta. & RDV';
export const MES_COMMANDES_PLANNING_LINK_LABEL = 'Voir toutes les prestations & RDV →';

export const MES_COMMANDES_COMPACT_RDV_LIMIT = 10;

/**
 * Capacités boutique (flags commerçant existants — pas basé sur le volume de commandes).
 * @param {{ moduleProduits?: boolean, modulePrestations?: boolean }|null|undefined} commercant
 * @param {{ loaded?: boolean }} [opts]
 */
export function resolveMesCommandesMerchantCapabilities(commercant, { loaded = true } = {}) {
  if (!loaded) {
    return {
      loaded: false,
      productsEnabled: true,
      servicesEnabled: true,
      isMixed: true,
      isProductOnly: false,
      isServiceOnly: false,
    };
  }
  const productsEnabled = commercant?.moduleProduits ?? true;
  const servicesEnabled = commercant?.modulePrestations ?? false;
  return {
    loaded: true,
    productsEnabled,
    servicesEnabled,
    isMixed: productsEnabled && servicesEnabled,
    isProductOnly: productsEnabled && !servicesEnabled,
    isServiceOnly: !productsEnabled && servicesEnabled,
  };
}

/** Filtres rapides visibles selon capacités (Tout + Actions requises toujours si applicable). */
export function getMesCommandesVisibleQuickFilters(caps) {
  const list = [MES_COMMANDES_QUICK_VIEW.ALL];
  if (caps?.productsEnabled) list.push(MES_COMMANDES_QUICK_VIEW.COMMANDES);
  if (caps?.servicesEnabled) list.push(MES_COMMANDES_QUICK_VIEW.RDV);
  list.push(MES_COMMANDES_QUICK_VIEW.ACTIONS);
  return list;
}

export function normalizeMesCommandesQuickView(quickView, caps) {
  if (!caps?.loaded) return quickView;
  if (quickView === MES_COMMANDES_QUICK_VIEW.COMMANDES && !caps.productsEnabled) {
    return caps.servicesEnabled ? MES_COMMANDES_QUICK_VIEW.RDV : MES_COMMANDES_QUICK_VIEW.ALL;
  }
  if (quickView === MES_COMMANDES_QUICK_VIEW.RDV && !caps.servicesEnabled) {
    return caps.productsEnabled ? MES_COMMANDES_QUICK_VIEW.COMMANDES : MES_COMMANDES_QUICK_VIEW.ALL;
  }
  return quickView;
}

export function mesCommandesSubtitleForCapabilities(caps) {
  if (!caps?.loaded || caps.isMixed) {
    return 'Suivi opérationnel — commandes produits et prestations & rendez-vous';
  }
  if (caps.isServiceOnly) {
    return 'Suivi opérationnel — prestations & rendez-vous';
  }
  return 'Suivi opérationnel — commandes produits';
}

export function isRendezVousCommande(commande) {
  return commande?.type === 'RENDEZ_VOUS';
}

export function isProductCommande(commande) {
  return !isRendezVousCommande(commande);
}

export function getCommandeProductLines(commande) {
  if (!commande || isRendezVousCommande(commande)) return [];
  const lines = commande.produits;
  return Array.isArray(lines) ? lines : [];
}

export function getFirstProductLine(commande) {
  const lines = getCommandeProductLines(commande);
  return lines[0] || null;
}

/** Nombre total de lignes / références produit (liste admin — agrégat API si présent). */
export function getCommandeTotalProductLines(commande) {
  const raw = commande?.totalProductLines;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 && Number.isInteger(raw)) {
    return raw;
  }
  return null;
}

/** Badge « +N » : autres références après la première (totalProductLines, pas le preview tronqué). */
export function getMultiProductExtraCount(commande) {
  const totalLines = getCommandeTotalProductLines(commande);
  if (totalLines != null) {
    return totalLines <= 1 ? 0 : totalLines - 1;
  }
  const lines = getCommandeProductLines(commande);
  if (lines.length <= 1) return 0;
  return lines.length - 1;
}

/** Quantité d'une ligne commande (liste) — défaut 1 si absente ou invalide. */
export function getCommandeLineArticleQuantity(line) {
  const raw = line?.quantite;
  const q = Number(raw);
  if (!Number.isFinite(q) || q <= 0) return 1;
  return Math.round(q);
}

/** Nombre total d'articles = somme des quantités sur toute la commande (agrégat API si présent). */
export function getCommandeTotalArticleQuantity(commande) {
  const raw = commande?.totalArticleQuantity;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 && Number.isInteger(raw)) {
    return raw;
  }
  const lines = getCommandeProductLines(commande);
  if (lines.length === 0) return 0;
  return lines.reduce((sum, line) => sum + getCommandeLineArticleQuantity(line), 0);
}

export function getCommandeProductLineDisplayName(line) {
  const name = line?.produit?.nom || line?.designationSnapshot;
  if (typeof name === 'string' && name.trim()) return name.trim();
  return null;
}

/**
 * Textes cellule liste « Produits » — titre = N article(s), sous-titre = aperçu noms (max 2 lignes + +N).
 * N'inclut pas commentaires, options ou composition (réservés au modal).
 */
export function buildCommandeProductListCellDisplay(commande) {
  const lines = getCommandeProductLines(commande);
  const totalQty = getCommandeTotalArticleQuantity(commande);

  if (lines.length === 0 || totalQty <= 0) {
    return {
      articleCount: 0,
      primaryText: 'Commande produits',
      previewText: null,
    };
  }

  const primaryText = totalQty === 1 ? '1 article' : `${totalQty} articles`;

  const labels = lines.map((line) => getCommandeProductLineDisplayName(line) || 'Produit');

  let previewText = null;
  const totalProductLines = getCommandeTotalProductLines(commande);
  const lineCountForSecondary = totalProductLines ?? lines.length;

  if (lines.length === 1) {
    previewText = labels[0];
  } else {
    const head = labels.slice(0, 2).join(' · ');
    const displayedNamesCount = Math.min(2, lines.length);
    const extraLines = Math.max(lineCountForSecondary - displayedNamesCount, 0);
    previewText = extraLines > 0 ? `${head} · +${extraLines}` : head;
  }

  return {
    articleCount: totalQty,
    primaryText,
    previewText,
  };
}

export function getPrestationAccentColor(commande) {
  const raw = commande?.prestation?.couleur;
  if (typeof raw === 'string' && /^#[0-9A-Fa-f]{3,8}$/.test(raw.trim())) {
    return raw.trim();
  }
  return '#6366f1';
}

/** Blocs maquette visibles selon filtre rapide + capacités commerçant (présentation uniquement). */
export function shouldShowMesCommandesProductBlock(quickView, caps = null) {
  if (caps?.loaded && !caps.productsEnabled) return false;
  return (
    quickView === MES_COMMANDES_QUICK_VIEW.ALL ||
    quickView === MES_COMMANDES_QUICK_VIEW.COMMANDES ||
    quickView === MES_COMMANDES_QUICK_VIEW.ACTIONS
  );
}

export function shouldShowMesCommandesRdvBlock(quickView, caps = null) {
  if (caps?.loaded && !caps.servicesEnabled) return false;
  return (
    quickView === MES_COMMANDES_QUICK_VIEW.ALL ||
    quickView === MES_COMMANDES_QUICK_VIEW.RDV ||
    quickView === MES_COMMANDES_QUICK_VIEW.ACTIONS
  );
}

export function filterCommandesByQuickView(commandes, quickView) {
  const list = Array.isArray(commandes) ? commandes : [];
  if (quickView === MES_COMMANDES_QUICK_VIEW.COMMANDES) {
    return list.filter(isProductCommande);
  }
  if (quickView === MES_COMMANDES_QUICK_VIEW.RDV) {
    return list.filter(isRendezVousCommande);
  }
  if (quickView === MES_COMMANDES_QUICK_VIEW.ACTIONS) {
    return list.filter(isMesCommandesActionRequise);
  }
  return list;
}

export function sortRendezVousByDateAsc(items) {
  return [...items].sort((a, b) => {
    const da = a?.dateDebut ? new Date(a.dateDebut).getTime() : Number.MAX_SAFE_INTEGER;
    const db = b?.dateDebut ? new Date(b.dateDebut).getTime() : Number.MAX_SAFE_INTEGER;
    return da - db;
  });
}

export function takeCompactRendezVousList(rdvList, limit = MES_COMMANDES_COMPACT_RDV_LIMIT) {
  const sorted = sortRendezVousByDateAsc(rdvList);
  return sorted.slice(0, Math.max(0, limit));
}

/** Liste RDV « en cours » affichée — compacte (10) ou complète si expanded. */
export function resolveMesCommandesRdvEnCoursDisplayed(
  rdvList,
  expanded,
  limit = MES_COMMANDES_COMPACT_RDV_LIMIT,
) {
  const sorted = sortRendezVousByDateAsc(rdvList);
  if (expanded || sorted.length <= limit) return sorted;
  return sorted.slice(0, Math.max(0, limit));
}

export const MES_COMMANDES_RDV_REDUCE_LIST_LABEL = 'Réduire la liste';

/** Libellé bouton expansion inline (sans navigation Planning). */
export function buildMesCommandesRdvExpandActionLabel(remaining) {
  const n = Math.max(0, Number(remaining) || 0);
  if (n <= 0) return null;
  if (n === 1) return "Voir l'autre prestation & RDV";
  return `Voir les ${n} autres prestations & RDV`;
}

/**
 * Texte footer bloc RDV en cours (compteur + état compact / étendu).
 * @returns {{ showFooter: boolean, summaryText: string|null, showExpand: boolean, showReduce: boolean, expandLabel: string|null }}
 */
export function buildMesCommandesRdvEnCoursFooterState(
  totalCount,
  displayedCount,
  expanded,
  limit = MES_COMMANDES_COMPACT_RDV_LIMIT,
) {
  const total = Math.max(0, Number(totalCount) || 0);
  const displayed = Math.max(0, Number(displayedCount) || 0);
  if (total <= limit) {
    return { showFooter: false, summaryText: null, showExpand: false, showReduce: false, expandLabel: null };
  }
  if (expanded) {
    return {
      showFooter: true,
      summaryText: `${total} créneaux affichés sur ${total}`,
      showExpand: false,
      showReduce: true,
      expandLabel: null,
    };
  }
  const remaining = total - displayed;
  return {
    showFooter: true,
    summaryText: `${displayed} prochains créneaux affichés sur ${total}`,
    showExpand: remaining > 0,
    showReduce: false,
    expandLabel: buildMesCommandesRdvExpandActionLabel(remaining),
  };
}

export function formatRdvDureeLabel(commande) {
  const minutes = commande?.prestation?.dureeMinutes;
  if (minutes == null || Number.isNaN(Number(minutes))) return null;
  const n = Number(minutes);
  if (n <= 0) return null;
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** Résolution URL image (sans dépendance Vite) — tests + fallback. */
export function resolveListItemImageUrl(item) {
  if (!item) return null;
  const raw =
    item.imageUrl ||
    (Array.isArray(item.images) && item.images.length > 0 && item.images[0]) ||
    item.image ||
    (Array.isArray(item.photos) && item.photos.length > 0 && item.photos[0]) ||
    null;
  if (!raw || typeof raw !== 'string') return null;
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) {
    return raw;
  }
  return raw;
}

export function resolveCommandeProductImageUrl(commande) {
  const first = getFirstProductLine(commande);
  const produit = first?.produit || first;
  return resolveListItemImageUrl(produit);
}

export function resolvePrestationImageUrl(commande) {
  return resolveListItemImageUrl(commande?.prestation);
}
